import { useState, useEffect, useRef, useCallback } from "react";
import { Plus, Search, X } from "lucide-react";
import toast from "react-hot-toast";
import MasterModal from "./MasterModal";
import MasterTable from "./MasterTable";
import { getZones, createZone, updateZone, toggleZoneActive } from "@/services/masters";
import AddZoneForm, { AddZoneFormHandle } from "./zones/AddZone";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import MasterTableHeader from "./generics/MasterTableHeader";

interface ZoneItem {
  Name: string;
  Description?: string;
  id?: string | number;
  Is_Active?: boolean;
}

interface Zone {
  id?: string | number;
  name: string;
  description?: string;
  isActive: boolean;
}

export default function Zones() {
  const { canCreate, canUpdate } = useScreenPermission("zones");
  const [zones, setZones] = useState<Zone[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [togglingId, setTogglingId] = useState<string | number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const formRef = useRef<AddZoneFormHandle>(null);
  const [totalCount,setTotalCount]=useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  const loadZones = useCallback(async (p: number = 1, search: string = "", statusFilter: string ) => {
    setIsLoading(true);
    try {
      const { data, status } = await getZones(p, search,statusFilter);
      if (status === 200 && data) {
        const d = data as any;
        const items: ZoneItem[] = d.results ?? (Array.isArray(d) ? d : []);
        setZones(items.map((item) => ({
          id: item.id,
          name: item.Name,
          description: item.Description || "",
          isActive: item.Is_Active ?? true,
        })));
        setTotalPages(d.total_pages ?? 1);
        setTotalCount(d.count ?? 0);
      } else {
        // toast.error("Failed to load zones");
      }
    } catch (error) {
      toast.error("Error loading zones");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load + debounced search
  useEffect(() => {
    const delay = searchQuery ? 400 : 0;
    const timer = setTimeout(() => {
      setPage(1);
      loadZones(1, searchQuery, statusFilter);
    }, delay);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, loadZones]);

  const handleOpenModal = (zone?: Zone) => {
    if (zone) {
      setEditingZone(zone);
    } else {
      setEditingZone(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingZone(null);
  };

  const handleSubmit = async (data: { Name: string; Description?: string }) => {
    formRef.current?.clearFieldErrors();
    setIsLoading(true);

    try {
      const response = editingZone && editingZone.id
        ? await updateZone(Number(editingZone.id), { Name: data.Name, Description: data.Description })
        : await createZone({ Name: data.Name, Description: data.Description });

        if (response.status === 201 || response.status === 200) {
        toast.success(editingZone ? "Zone updated successfully" : "Zone created successfully");
        handleCloseModal();
        await loadZones(page, searchQuery, statusFilter);
      } else if (response.errors) {
        let hasFieldErrors = false;

        if (typeof response.errors === "object") {
          Object.entries(response.errors).forEach(([key, value]) => {
            const errorText = Array.isArray(value) ? value[0] : String(value);

            if (key === "error" || key === "non_field_errors" || key === "detail") {
              toast.error(errorText);
            } else {
              formRef.current?.setFieldError(key, errorText);
              hasFieldErrors = true;
            }
          });
        }

        if (!hasFieldErrors) {
          toast.error("Failed to save zone.");
        }
      }
    } catch (error) {
      console.error("Error saving zone:", error);
      toast.error("Error saving zone");
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalSubmit = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const handleToggleActive = async (zone: Zone) => {
    if (!zone.id) return;
    setTogglingId(zone.id);
    try {
      const response = await toggleZoneActive(Number(zone.id), { Is_Active: !zone.isActive });
      if (response.status === 200) {
        toast.success(`Zone ${!zone.isActive ? "activated" : "deactivated"} successfully`);
        await loadZones(page, searchQuery, statusFilter);
      } else {
        toast.error("Failed to update zone status");
      }
    } catch (error) {
      toast.error("Error updating zone status");
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    { key: "name" as const, label: "Zone Name" },
    { key: "description" as const, label: "Description" },
  ];

  return (
    <div className="space-y-6">
      <MasterTableHeader
        title="Zones Management"
        description="Create, edit, and manage zones"
        canCreate={canCreate}
        addButtonText="Add New Zone"
        onAdd={() => handleOpenModal()}
        searchPlaceholder="Search by zone name or description..."
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        showDropdownFilter
        dropdownValue={statusFilter}
        onDropdownChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}
        showClearFilters
        onClearFilters={() => {
          setSearchQuery("");
          setStatusFilter("");
          setPage(1);
      
          loadZones(1, "", "");
        }}
      />

      <MasterTable
        columns={columns}
        data={zones}
        onEdit={handleOpenModal}
        onToggleActive={handleToggleActive}
        togglingId={togglingId}
        isLoading={isLoading && zones.length === 0}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(p) => { setPage(p); loadZones(p, searchQuery, statusFilter); }}
        canEdit={canUpdate}
        canToggle={canUpdate}
        totalCount={totalCount}
      />

      <MasterModal
        isOpen={isModalOpen}
        title={editingZone ? "Edit Zone" : "Add New Zone"}
        onClose={handleCloseModal}
        onSubmit={handleModalSubmit}
        isLoading={isLoading}
      >
        <AddZoneForm
          ref={formRef}
          onSubmit={handleSubmit}
          defaultValues={
            editingZone
              ? { Name: editingZone.name, Description: editingZone.description }
              : undefined
          }
          isLoading={isLoading}
        />
      </MasterModal>
    </div>
  );
}
